import { memo, useCallback } from 'react';
import { StyleSheet, Text, View } from 'react-native';
import { TouchableRipple } from 'react-native-paper';

import { StatusBadge } from '@/components/atoms/badge';
import { NotificationDot } from '@/components/atoms/indicator';
import { ActionLinkRow, type ActionLinkItem } from '@/components/molecules/action-link';
import { formatDateOnly } from '@/formatters/formatDateOnly';
import { formatTimeOnly } from '@/formatters/formatTimeOnly';
import { useTranslation } from '@/i18n';
import {
  getHandoffAction,
  getHandoffBadges,
  getHandoffCardDescription,
  type HandoffAction,
  type HandoffItem,
} from '@/models/handoffs';
import { colorSemantic, radii, rippleOnSurface, spacing, unisonColors, useAppTheme } from '@/theme';

const UNREAD_DOT_SIZE = 13;

interface AiBatonListItemProps {
  readonly item: HandoffItem;
  readonly currentUserUid: string | null | undefined;
  readonly isUpdating: boolean;
  readonly onClaim: (handoffId: string) => void;
  readonly onRelease: (handoffId: string) => void;
  readonly onPress?: (handoffId: string) => void;
}

interface BuildActionsInput {
  readonly action: HandoffAction | null;
  readonly isUpdating: boolean;
  readonly t: (key: string, options?: Record<string, unknown>) => string;
  readonly onClaim: () => void;
  readonly onRelease: () => void;
}

const buildActions = ({
  action,
  isUpdating,
  t,
  onClaim,
  onRelease,
}: BuildActionsInput): readonly ActionLinkItem[] => {
  if (action === null) {
    return [];
  }

  if (action === 'claim') {
    return [{ label: t('aiTasks.action.claim'), onPress: onClaim, disabled: isUpdating }];
  }

  return [{ label: t('aiTasks.action.release'), onPress: onRelease, disabled: isUpdating }];
};

const AiBatonListItemBase = ({
  item,
  currentUserUid,
  isUpdating,
  onClaim,
  onRelease,
  onPress,
}: AiBatonListItemProps) => {
  const { t } = useTranslation();
  const { colors } = useAppTheme();

  const handleClaim = useCallback(() => {
    onClaim(item.id);
  }, [item.id, onClaim]);

  const handleRelease = useCallback(() => {
    onRelease(item.id);
  }, [item.id, onRelease]);

  const handlePress = useCallback(() => {
    onPress?.(item.id);
  }, [item.id, onPress]);

  const badges = getHandoffBadges({
    status: item.status,
    userUid: item.userUid,
    currentUserUid,
    handoffType: item.handoffType,
    severity: item.severity,
    t,
  });

  const description = getHandoffCardDescription({
    handoffType: item.handoffType,
    notes: item.notes,
    contactName: item.contact?.fullName,
    t,
  });

  const action = getHandoffAction({ status: item.status });

  const actions = buildActions({
    action,
    isUpdating,
    t,
    onClaim: handleClaim,
    onRelease: handleRelease,
  });

  return (
    <View testID={`ai-baton-list-item-${item.id}`}>
      <View style={styles.container}>
        <TouchableRipple
          accessibilityRole="button"
          rippleColor={rippleOnSurface}
          style={[styles.content, actions.length === 0 && styles.contentNoActions]}
          testID={`ai-baton-list-item-${item.id}-body`}
          onPress={handlePress}>
          <View style={styles.contentInner}>
            <View style={styles.headerRow}>
              <View style={styles.badgesContainer}>
                {badges.map((badge) => (
                  <StatusBadge
                    key={`${badge.label}-${badge.variant}`}
                    label={badge.label}
                    variant={badge.variant}
                  />
                ))}
              </View>
              <View style={styles.dateTimeContainer}>
                <Text style={[styles.time, { color: colors.textNdPrimary }]}>
                  {formatDateOnly(item.createdAt)}
                </Text>
                <Text style={[styles.time, { color: colors.textNdPrimary }]}>
                  {formatTimeOnly(item.createdAt)}
                </Text>
              </View>
            </View>
            {description ? (
              <Text style={[styles.body, { color: colors.textNdPrimary }]}>{description}</Text>
            ) : null}
          </View>
        </TouchableRipple>
        {actions.length > 0 ? (
          <View style={styles.actionsContainer}>
            <ActionLinkRow actions={actions} testID={`ai-baton-list-item-${item.id}-actions`} />
          </View>
        ) : null}
      </View>
      {item.unreadDispatch ? (
        <View pointerEvents="none" style={styles.unreadDot}>
          <NotificationDot
            borderColor={unisonColors.white}
            borderWidth={2}
            color={colorSemantic.colorBgErrorStrong}
            size={UNREAD_DOT_SIZE}
            testID={`ai-baton-list-item-${item.id}-unread-dot`}
          />
        </View>
      ) : null}
    </View>
  );
};

export const AiBatonListItem = memo(AiBatonListItemBase);

const styles = StyleSheet.create({
  container: {
    backgroundColor: unisonColors.white,
    borderRadius: radii.md,
    overflow: 'hidden',
  },
  // The 13px dot straddles the card's top-left corner, 4px left and 4px above
  // the edge.
  unreadDot: {
    left: -4,
    position: 'absolute',
    top: -4,
    zIndex: 1,
  },
  content: {
    paddingTop: spacing[4],
    paddingHorizontal: spacing[4],
    paddingBottom: spacing[2],
  },
  contentInner: {
    rowGap: spacing[2],
  },
  contentNoActions: {
    paddingBottom: spacing[4],
  },
  actionsContainer: {
    paddingHorizontal: spacing[4],
  },
  headerRow: {
    flexDirection: 'row',
    justifyContent: 'space-between',
    alignItems: 'flex-start',
    columnGap: spacing[3],
  },
  badgesContainer: {
    flex: 1,
    flexDirection: 'row',
    flexWrap: 'wrap',
    columnGap: spacing[1],
    rowGap: spacing[1],
  },
  dateTimeContainer: {
    alignItems: 'flex-end',
  },
  time: {
    fontSize: 11,
    fontWeight: '500',
    lineHeight: 16,
    letterSpacing: 0.5,
    textAlign: 'right',
  },
  body: {
    fontSize: 16,
    fontWeight: '400',
    lineHeight: 24,
    letterSpacing: 0.5,
  },
});
